import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import { useAppDispatch } from '@/store/hooks'
import { loadExistingRequestData } from '@/store/slices/formSlice'
import { getOrderByPatient, OrderStatus } from '@/utils/DatabaseOperations'
import React, { useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

let _state: string = ''

/**
 * SMART on FHIR Callback page that handles access token request
 * @returns {JSX.Element} The Callback component
 */
const Callback: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const searchParams = useSearchParams().at(0) as URLSearchParams
  const authCode = searchParams.get('code') || ''
  const state = searchParams.get('state') || ''

  if (_state === '') {
    _state = state
  }

  const SmartOnFHIRAccess = useCallback(async () => {
    try {
      const client = EpicAuthClient.getInstance()
      const accessContext = await client.retrieveSmartOnFHIRAccessContext(authCode, state)
      const existingRequest = await getOrderByPatient(accessContext.fhirPatientId)

      switch (existingRequest.status) {
        case OrderStatus.COMPLETE:
          if (existingRequest.json_payload?.requisitionFormData) {
            const formData = existingRequest.json_payload.requisitionFormData

            // Load the existing request data into Redux store
            dispatch(loadExistingRequestData(formData))
            navigate('/form/complete', { state })
          }
          break
        case OrderStatus.DRAFT:
          if (existingRequest.json_payload?.requisitionFormData) {
            try {
              // Validate the existing request data structure
              const formData = existingRequest.json_payload.requisitionFormData

              // Load the existing request data into Redux store
              dispatch(loadExistingRequestData(formData))
              // Navigate to the form with the loaded data
              navigate('/form', { state })
            } catch (validationError) {
              console.error('Error validating existing request data:', validationError)
              // Fall back to new request if validation fails
              navigate('/form', { state })
            }
          } else {
            console.log('No existing request payload found, treating as new request')
            navigate('/form', { state })
          }
          break
        case 'new':
        default:
          navigate('/form', { state })
          break
      }
    } catch (error) {
      console.error('Login error:', error)
      // On error, navigate to form anyway to allow user to continue
      navigate('/form', { state })
    }
  }, [authCode, state, navigate, dispatch])

  // Automatically trigger the access request when component mounts with proper validation
  useEffect(() => {
    // Only proceed if we have valid parameters and we're not already loading
    if (authCode && state) {
      const timer = setTimeout(() => {
        SmartOnFHIRAccess()
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [SmartOnFHIRAccess, authCode, state])

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md p-8">
        <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-lg">
          <div className="mt-8">
            <div className="text-center">
              <div
                className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              >
                <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                  Loading...
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-600">Requesting access context...</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Callback
